import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';

import WithListObservers from '../../../hoc/with-list-observers';
import { IUser } from '../../../types/interfaces/user';
import NoResults from '../no-results';
import Search from '../search/Search';
import UserListItemSkeleton from '../skeletons/user-list-item-skeleton/UserListItemSkeleton';
import { useStyles } from './UsersList.styles';
import UserBigItem from './user-big-item/UserBigItem';

const DEFAULT_USER_LIST_SKELETON_COUNT = 4;

export interface IUsersListBigProps {
  users?: IUser[] | null;
  communityId?: string;
  showSearch?: boolean;
  listFooter?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  skeletonCount?: number;
  withoutTabs?: boolean;
}

export default function UsersListBig(props: IUsersListBigProps) {
  const {
    users,
    communityId,
    showSearch = false,
    listFooter,
    isLoading = false,
    isError = false,
    skeletonCount = DEFAULT_USER_LIST_SKELETON_COUNT,
    withoutTabs = false,
  } = props;
  const { classes } = useStyles();
  const [filterValue, setFilterValue] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>(null);

  useEffect(() => {
    if (!users) {
      setFilteredUsers(null);
      return;
    }
    if (!filterValue) {
      setFilteredUsers(users);
    } else {
      setFilteredUsers(
        users.filter(
          (u) =>
            u.firstname?.toLowerCase().indexOf(filterValue.toLowerCase()) !==
              -1 ||
            u.lastname?.toLowerCase().indexOf(filterValue.toLowerCase()) !== -1,
        ),
      );
    }
  }, [filterValue, users]);

  const awaitingListData = users == null && !isError;
  const showSkeleton = Boolean(isLoading) || awaitingListData;
  const showNoResult =
    !showSkeleton && !!filteredUsers && !filteredUsers?.length;
  const listClassName = withoutTabs
    ? classes.userBigItemWrapperWithoutTabs
    : classes.userBigItemWrapper;

  return (
    <div className={classes.usersListWrapper}>
      {showSearch && (
        <Search
          fullWidth
          value={filterValue}
          onChange={setFilterValue}
          className={classes.usersListSearch}
        />
      )}
      {showSkeleton && (
        <Box className={listClassName}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <UserListItemSkeleton key={`user-list-skeleton-${index}`} />
          ))}
          {listFooter}
        </Box>
      )}
      {!showSkeleton && showNoResult && <NoResults />}
      {!showSkeleton && !showNoResult && (
        <WithListObservers wrapperClassName={listClassName}>
          {filteredUsers?.map((u) => (
            <React.Fragment key={u.id}>
              <UserBigItem user={u} communityId={communityId} />
            </React.Fragment>
          ))}
          {listFooter}
        </WithListObservers>
      )}
    </div>
  );
}
